import React, { type FunctionComponent } from 'react'
import { type SimpleOption } from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import { InsightsPortalSectionLayout } from 'domains/insights-portal/utils/insights-portal.styles'
import { InsightsPortalSchema } from 'domains/insights-portal/api'
import { useDomainMessages } from 'domains/insights-portal/utils/domain/intl'
import { OrderDeliveryMetrics } from 'domains/insights-portal/schemas/order-deliveries/insightsPortalOrderDeliveries.types'
import { AggWarehousePerformanceMetrics } from 'domains/insights-portal/schemas/aggregate-warehouse-performance/insightsPortalAggWarehousePerformance.types'
import {
  useOrderDeliveriesMetricLabels,
  useOrderDeliveryMetricValueFormatter,
} from 'domains/insights-portal/schemas/order-deliveries/insightsPortalOrderDeliveries.hooks'
import { useFormatValue } from 'domains/insights-portal/utils/format-value.hooks'
import { KpiOverviewTrend } from 'domains/insights-portal/widgets/KPIOverviewTrend/KpiOverviewTrend'
import { useOrderItemsMetricLabels } from 'domains/insights-portal/schemas/order-items/insightsPortalOrderItems.hooks'

interface SummaryComponentProps {
  filtersObjOverride?: { [key: string]: SimpleOption[] }
}

export const SummaryPerformance: FunctionComponent<SummaryComponentProps> = ({
  filtersObjOverride,
}) => {
  const { formatter } = useOrderDeliveryMetricValueFormatter()
  const { formatCurrency, formatInteger } = useFormatValue()
  const labels = {
    ...useOrderDeliveriesMetricLabels(),
    ...useOrderItemsMetricLabels(),
  }

  const tooltips = useDomainMessages({
    SUM_GMV: 'insightsPortalDomain.performanceDashboard.tooltips.SUM_GMV',
    COUNT_ORDER: 'insightsPortalDomain.performanceDashboard.tooltips.COUNT_ORDER',
    AVG_BASKET: 'insightsPortalDomain.basketDashboard.tooltips.AVG_BASKET',
    AVG_RATING: 'insightsPortalDomain.performanceDashboard.tooltips.AVG_RATING',
    COUNT_FIRST_ORDER: 'insightsPortalDomain.performanceDashboard.tooltips.COUNT_FIRST_ORDER',
  })

  const groupbylabels = useDomainMessages({
    days: 'insightsPortalDomain.pillTabs.days.label',
  })

  const groupByOptions = [{ label: groupbylabels.days, value: 'DATE_PT' }]

  const trendChartLabels = useDomainMessages({
    title: 'insightsPortalDomain.SummaryPerformance.trend.title',
  })

  return (
    <InsightsPortalSectionLayout>
      <KpiOverviewTrend
        showExportMenu={true}
        metricInfo={[
          {
            apiMetric: AggWarehousePerformanceMetrics.SUM_GMV,
            metricName: labels[AggWarehousePerformanceMetrics.SUM_GMV],
            desiredDirection: 'positive',
            formatter: v => formatCurrency(v, false),
            tooltip: tooltips[AggWarehousePerformanceMetrics.SUM_GMV],
            historicalMetric: 'SUM_GMV_HISTORICAL',
          },

          {
            apiMetric: AggWarehousePerformanceMetrics.COUNT_ORDER,
            metricName: labels[AggWarehousePerformanceMetrics.COUNT_ORDER],
            desiredDirection: 'positive',
            formatter: v => formatInteger(v, false),
            tooltip: tooltips[AggWarehousePerformanceMetrics.COUNT_ORDER],
            historicalMetric: 'COUNT_ORDER_HISTORICAL',
          },
          {
            apiMetric: AggWarehousePerformanceMetrics.AVG_BASKET,
            metricName: labels[AggWarehousePerformanceMetrics.AVG_BASKET],
            desiredDirection: 'positive',
            formatter: v => formatCurrency(v, false),
            tooltip: tooltips[AggWarehousePerformanceMetrics.AVG_BASKET],
            historicalMetric: 'AVG_BASKET_HISTORICAL',
          },
          {
            apiMetric: AggWarehousePerformanceMetrics.AVG_RATING,
            metricName: labels[AggWarehousePerformanceMetrics.AVG_RATING],
            desiredDirection: 'positive',
            formatter: v => formatter(OrderDeliveryMetrics.AVG_RATING, v),
            tooltip: tooltips[AggWarehousePerformanceMetrics.AVG_RATING],
            historicalMetric: 'AVG_RATING_HISTORICAL',
          },
          {
            apiMetric: AggWarehousePerformanceMetrics.COUNT_FIRST_ORDER,
            metricName: labels[AggWarehousePerformanceMetrics.COUNT_FIRST_ORDER],
            desiredDirection: 'positive',
            formatter: v => formatInteger(v, false),
            tooltip: tooltips[AggWarehousePerformanceMetrics.COUNT_FIRST_ORDER],
            historicalMetric: 'COUNT_FIRST_ORDER_HISTORICAL',
          },
        ]}
        schema={InsightsPortalSchema.AggWarehouseLocationPerformanceHistoricalGrowth}
        groupBys={groupByOptions}
        title={trendChartLabels.title}
        filtersObjOverride={filtersObjOverride}
      />
    </InsightsPortalSectionLayout>
  )
}
